import { Flag } from 'lucide-react'
import { memo } from 'react'
import { Link } from '#router'
import { Input } from '@/components/ui/input'
import {
  type ArchiveGroup,
  AUDITION_LANES,
  LANE_LABELS,
  type QueueSort,
  toArchiveGroup,
} from '@/lib/audition/queue'
import type { RouterOutputs } from '@/lib/trpc'
import type { ReviewLens } from './hooks/use-auditions'
import { CallTag, PaidTag, StageTag, WaitLabel } from './labels'

/**
 * One row as either queue surface renders it. `list` and `browse` share the
 * row builder on the server, so the two pages share one type here.
 */
export type QueueRow =
  RouterOutputs['admin']['auditions']['list']['rows'][number]

/** A lane, or the deliberate "hasn't picked one" answer. */
export type LaneFilter = keyof typeof LANE_LABELS | 'none'

/** Where the live queue lives — the way back from everything under it. */
export const QUEUE_HREF = '/admin/auditions'

/**
 * Where one archive lives, optionally with a search already typed. The queue
 * page sends a fruitless search here; the archive page's group chips send
 * the admin between archives.
 */
export function archiveHref(group: ArchiveGroup, q?: string): string {
  const base = `/admin/auditions/archive/${group}`
  return q ? `${base}?q=${encodeURIComponent(q)}` : base
}

/**
 * The archive a row was opened from, with the search that found it, so the
 * detail page can send the admin back to the same list (LG-1052).
 */
export interface ArchiveOrigin {
  group: ArchiveGroup
  q?: string
}

/**
 * Where one applicant's review lives, optionally remembering the way back.
 *
 * The origin travels as its PARTS — `?from=<group>&q=<search>` — never as a
 * whole URL to be re-parsed on the other side. A group name is read with
 * `toArchiveGroup` and either is one of ours or is nothing, so the detail
 * page's read is total without a URL parser (whose throws on a mangled
 * authority would have landed in render) and there is no path to follow, so
 * nothing to guard against following. {@link readArchiveOrigin} is the
 * other half; the two sit together so the wire format is spelled once.
 */
export function detailHref(id: string, origin?: ArchiveOrigin): string {
  const base = `/admin/auditions/${id}`
  if (!origin) return base
  const params = new URLSearchParams({ from: origin.group })
  if (origin.q) params.set('q', origin.q)
  return `${base}?${params}`
}

/**
 * The origin {@link detailHref} wrote into a detail page's search, or nothing
 * when there is none — including a `from` the URL bar made up, which names
 * no archive of ours and so is not followed. An empty `q` is no search.
 */
export function readArchiveOrigin(
  params: URLSearchParams,
): ArchiveOrigin | undefined {
  const group = toArchiveGroup(params.get('from'))
  if (!group) return undefined
  const q = params.get('q')
  return q ? { group, q } : { group }
}

/**
 * One labelled band of controls.
 *
 * The heading is not decoration. Rows of identical pills read as one
 * undifferentiated wall, and an admin cannot tell that the stage row and the
 * facts row answer different questions — or that "Longest waiting" re-orders
 * the page while "Paid" removes rows from it. Naming each band is what makes
 * the grouping visible instead of merely true.
 *
 * The name sits above its controls rather than beside them so a band that
 * wraps to two lines still reads as one thing.
 */
export function ControlBand({
  label,
  hint,
  className,
  children,
}: {
  label: string
  /** Read-only context for the band, sat beside its name rather than adrift. */
  hint?: React.ReactNode
  className?: string
  children: React.ReactNode
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className={`space-y-1.5 ${className ?? ''}`}
    >
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-sm font-medium">{label}</span>
        {hint}
      </div>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
  )
}

/** A toggleable filter pill. Pressed state is `aria-pressed`, not colour alone. */
export function FilterChip({
  active,
  onClick,
  children,
  title,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
  title?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={title}
      className={`rounded-md border px-2.5 py-1 text-xs transition-colors ${
        active
          ? 'border-primary bg-primary/10 text-foreground'
          : 'border-border bg-card text-muted-foreground hover:border-primary/50'
      }`}
    >
      {children}
    </button>
  )
}

/**
 * Memoized: both parents re-render on every search keystroke (the archive's
 * debounce gates its QUERY, not its render), and each render would otherwise
 * re-execute every visible row — up to the 500-row live cap. `row` keeps its
 * identity through react-query's cache, so surviving rows skip.
 */
export const QueueRowItem = memo(function QueueRowItem({
  row,
  lens,
  from,
}: {
  row: QueueRow
  lens: ReviewLens
  /**
   * The page to come back to from the detail view, when it is not the queue
   * — an archive, with its search (LG-1052). One object per settled search,
   * not per render: a parent that builds a fresh one each time defeats the
   * memo.
   */
  from?: ArchiveOrigin
}) {
  const lensLine =
    lens === 'level'
      ? row.levelLine
      : row.motivationLine
        ? `“${row.motivationLine}”`
        : ''

  return (
    <li>
      <Link
        href={detailHref(row.id, from)}
        className={`hover:bg-accent/50 grid grid-cols-1 items-center gap-2 rounded-md px-3 py-3 transition-colors md:grid-cols-[minmax(0,1fr)_150px_170px_minmax(0,1.1fr)] ${
          row.overdue ? 'bg-destructive/5' : ''
        }`}
      >
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">
              {`${row.firstName} ${row.lastName}`}
            </span>
            {row.hasPaid && <PaidTag />}
            {row.resubmitted && (
              <span
                className="text-muted-foreground text-xs"
                title="Re-entered the queue — the wait clock reset"
              >
                resubmitted
              </span>
            )}
            {row.flagCount > 0 && (
              <span
                className={`inline-flex items-center gap-1 text-xs ${
                  row.flagWorst === 'miss'
                    ? 'text-destructive'
                    : 'text-amber-600 dark:text-amber-500'
                }`}
                title="Open the application to see what went wrong"
              >
                <Flag aria-hidden="true" className="size-3" />
                {row.flagCount === 1 ? '1 flag' : `${row.flagCount} flags`}
              </span>
            )}
          </div>
          <div className="text-muted-foreground truncate text-xs">
            {row.email}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <StageTag stage={row.stage} />
          <CallTag call={row.call} stage={row.stage} />
        </div>

        <WaitLabel
          days={row.waitDays}
          overdue={row.overdue}
          appliedAt={row.appliedAt}
        />

        <div className="min-w-0">
          <div
            className={`text-sm ${row.needsAction ? 'text-foreground' : 'text-muted-foreground'}`}
          >
            {row.owed}
          </div>
          {lensLine && (
            <div className="text-muted-foreground truncate text-xs">
              {lensLine}
            </div>
          )}
        </div>
      </Link>
    </li>
  )
})

/** The two orders the server can serve, as one band. */
export function SortBand({
  sort,
  onChange,
}: {
  sort: QueueSort
  onChange: (sort: QueueSort) => void
}) {
  return (
    <ControlBand label="Sort">
      <FilterChip
        active={sort === 'longest_waiting'}
        onClick={() => onChange('longest_waiting')}
      >
        Longest waiting
      </FilterChip>
      <FilterChip active={sort === 'newest'} onClick={() => onChange('newest')}>
        Newest first
      </FilterChip>
    </ControlBand>
  )
}

/**
 * The lane an applicant took is a fact about them like Paid, and "has not
 * chosen" is a real answer rather than the absence of a filter — so it is a
 * chip of its own.
 */
export function LaneBand({
  lane,
  onChange,
}: {
  lane: LaneFilter | null
  onChange: (lane: LaneFilter | null) => void
}) {
  const toggle = (next: LaneFilter) => onChange(lane === next ? null : next)
  return (
    <ControlBand label="Path taken">
      {AUDITION_LANES.map((entry) => (
        <FilterChip
          key={entry}
          active={lane === entry}
          onClick={() => toggle(entry)}
          title={
            entry === 'fast'
              ? 'Paid before talking to anyone'
              : 'Talks first, pays after'
          }
        >
          {LANE_LABELS[entry]}
        </FilterChip>
      ))}
      <FilterChip
        active={lane === 'none'}
        onClick={() => toggle('none')}
        title="Has not chosen a path yet"
      >
        Path not chosen
      </FilterChip>
    </ControlBand>
  )
}

/** The two undecided selection criteria, as the row summary the admin reads. */
export function LensBand({
  lens,
  onChange,
  className,
}: {
  lens: ReviewLens
  onChange: (lens: ReviewLens) => void
  className?: string
}) {
  const title =
    'Selection criteria are still undecided — flip the lens to compare'
  return (
    <ControlBand label="Lens" className={className}>
      <FilterChip
        active={lens === 'level'}
        onClick={() => onChange('level')}
        title={title}
      >
        Level
      </FilterChip>
      <FilterChip
        active={lens === 'motivation'}
        onClick={() => onChange('motivation')}
        title={title}
      >
        Motivation
      </FilterChip>
    </ControlBand>
  )
}

/** One accessible name for the search box, so both pages answer "search name or email". */
export function SearchBox({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) {
  return (
    <Input
      type="search"
      className="h-8 w-56"
      placeholder="Search name or email"
      aria-label="Search name or email"
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  )
}
